import { nonNegativeNumber, positiveInteger } from "@/utils/form";
import { z } from "zod";
import { t } from "i18next";
import { addDays, isWithinInterval, parse } from "date-fns";

export const ShiftSchema = z.object({
  employeeQuantity: positiveInteger(t("modals:shift.inputs.employees.label")),
  // TODO: Remove this once the backend is updated
  subContractorQuantity: nonNegativeNumber(t("modals:shift.inputs.subcontractor.label")),
  startTime: z.string()
    .refine(
      (val) => /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(val),
      { message: t("schemas:shift.starttimerequired") }
    ),
  endTime: z.string()
    .refine(
      (val) => /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(val),
      { message: t("schemas:shift.endtimerequired") }
    ),
  holiday: z.boolean().default(false),
  monday: z.boolean().default(false),
  tuesday: z.boolean().default(false),
  wednesday: z.boolean().default(false),
  thursday: z.boolean().default(false),
  friday: z.boolean().default(false),
  saturday: z.boolean().default(false),
  sunday: z.boolean().default(false),
  applyHolidayPay: z.boolean(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  pauseStartTime: z.string().optional(),
  pauseEndTime: z.string().optional(),
}).check((ctx) => {
  const {
    holiday, monday, tuesday, wednesday, thursday, friday, saturday,
    sunday, startDate, endDate, applyHolidayPay, startTime, endTime,
    pauseStartTime, pauseEndTime
  } = ctx.value;

  // Check for day selection
  const isSelected = holiday || monday || tuesday || wednesday || thursday || friday || saturday || sunday;
  if (!isSelected) {
    ctx.issues.push({
      path: ["monday"],
      code: "custom",
      message: t("schemas:shift.selectdayrequired"),
      input: ctx.value.monday,
    });
  }

  // Check if end date is after start date
  if (startDate && endDate && endDate.getTime() < startDate.getTime()) {
    ctx.issues.push({
      code: "custom",
      path: ["endDate"],
      message: t("schemas:createtender.contract.enddateafterstartdate"),
      input: ctx.value.endDate,
    });
  }

  // Implement mutual exclusivity between holiday and applyHolidayPay
  if (holiday && applyHolidayPay) {
    // Add issue for holiday
    ctx.issues.push({
      code: "custom",
      path: ["holiday"],
      message: t("schemas:shift.holidayandholidaypayexclusive"),
      input: ctx.value.holiday,
    });
    // Add issue for applyHolidayPay
    ctx.issues.push({
      code: "custom",
      path: ["applyHolidayPay"],
      message: t("schemas:shift.holidayandholidaypayexclusive"),
      input: ctx.value.applyHolidayPay,
    });
  }

  // Ensure both pauseStartTime and pauseEndTime are provided together
  if ((pauseStartTime && !pauseEndTime) || (!pauseStartTime && pauseEndTime)) {
    if (!pauseStartTime) {
      ctx.issues.push({
        code: "custom",
        path: ["pauseStartTime"],
        message: t("schemas:shift.pausestarttimerequired"),
        input: ctx.value.pauseStartTime,
      });
    }
    if (!pauseEndTime) {
      ctx.issues.push({
        code: "custom",
        path: ["pauseEndTime"],
        message: t("schemas:shift.pauseendtimerequired"),
        input: ctx.value.pauseEndTime,
      });
    }
  }

  // Validate pause times if both are provided
  if (pauseStartTime && pauseEndTime) {
    const baseDate = new Date(0, 0, 0); // Use a fixed base date

    const parseTime = (time: string) => parse(time, "HH:mm", baseDate);

    const startDateTime = parseTime(startTime);
    let endDateTime = parseTime(endTime);
    let pauseStartDateTime = parseTime(pauseStartTime);
    let pauseEndDateTime = parseTime(pauseEndTime);

    // Check if shift spans to the next day
    if (endDateTime <= startDateTime) {
      endDateTime = addDays(endDateTime, 1);
    }

    // Adjust pause times for overnight shifts
    if (pauseStartDateTime <= startDateTime) {
      pauseStartDateTime = addDays(pauseStartDateTime, 1);
    }

    if (pauseEndDateTime <= startDateTime) {
      pauseEndDateTime = addDays(pauseEndDateTime, 1);
    }

    // Check if pause times are within shift times
    const isPauseStartWithinShift = isWithinInterval(pauseStartDateTime, { start: startDateTime, end: endDateTime });
    const isPauseEndWithinShift = isWithinInterval(pauseEndDateTime, { start: startDateTime, end: endDateTime });
    const isPauseOrderValid = pauseStartDateTime < pauseEndDateTime;

    if (!isPauseStartWithinShift) {
      ctx.issues.push({
        code: "custom",
        path: ["pauseStartTime"],
        message: t("schemas:shift.pausestarttimeinvalid"),
        input: ctx.value.pauseStartTime,
      });
    }

    if (!isPauseEndWithinShift) {
      ctx.issues.push({
        code: "custom",
        path: ["pauseEndTime"],
        message: t("schemas:shift.pauseendtimeinvalid"),
        input: ctx.value.pauseEndTime,
      });
    }

    if (!isPauseOrderValid) {
      ctx.issues.push({
        code: "custom",
        path: ["pauseStartTime", "pauseEndTime"],
        message: t("schemas:shift.pauseorderinvalid"),
        input: ctx.value.pauseStartTime,
      });
    }
  }
});

export const ShiftDefaultValues = {
  employeeQuantity: undefined,
  endTime: undefined,
  friday: false,
  holiday: false,
  monday: false,
  subContractorQuantity: 0,
  startTime: undefined,
  tuesday: false,
  wednesday: false,
  thursday: false,
  saturday: false,
  sunday: false,
  applyHolidayPay: false,
  startDate: undefined,
  endDate: undefined,
  pauseStartTime: undefined,
  pauseEndTime: undefined,
};

export type TShiftSchema = z.infer<typeof ShiftSchema>;
